// CRM Contact Record — colourful HubSpot-style record page with full ContactPanel parity.
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import API_BASE from '../../config';
import { creditAwareFetch } from '../../hooks/creditAwareFetch';
import CrmSequenceSettings from './CrmSequenceSettings';
import CrmEmailTimeline from './CrmEmailTimeline';
import CrmEmailComposer from './CrmEmailComposer';
import { DealDrawer } from './DealDrawer';

const STATUSES = ['new', 'contacted', 'replied', 'meeting booked', 'closed'];
const STATUS_PILL = { new: 'crmr-p-blue', contacted: 'crmr-p-blue', replied: 'crmr-p-amber', 'meeting booked': 'crmr-p-green', closed: 'crmr-p-slate' };
const SIGNAL = {
  funding: { c: '#056F4E', bg: '#D1F0E4', l: 'Funding', icon: '$' },
  leadership_change: { c: '#5B3FD6', bg: '#E7E1FD', l: 'Leadership', icon: '★' },
  hiring: { c: '#0B5BB5', bg: '#D9E9FB', l: 'Hiring', icon: '↗' },
  tech_change: { c: '#066576', bg: '#CFF0F6', l: 'Tech', icon: '⚙' },
  news: { c: '#3F4956', bg: '#E3E7ED', l: 'News', icon: '◆' },
};
function domainOf(url) { if (!url) return null; return url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0] || null; }
function initials(name) { return (name || '?').split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase(); }
const AVATAR_COLOURS = ['#7B1E2B', '#1E5A7B', '#2E7D32', '#6A1B9A', '#B8860B', '#00695C', '#C2410C', '#374151', '#9D174D', '#1565C0', '#4E342E', '#5B6C1E', '#7C5CFC', '#0F766E', '#B91C1C', '#5D4037', '#3949AB', '#00838F'];
function avatarColour(name) { const s = String(name || '?'); let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return AVATAR_COLOURS[h % AVATAR_COLOURS.length]; }
function scoreBand(v) { const s = Number(v) || 0; if (s >= 8) return { cls: 'crmr-band-hot', label: 'Hot' }; if (s >= 5) return { cls: 'crmr-band-warm', label: 'Warm' }; return { cls: 'crmr-band-cold', label: 'Cold' }; }
function ukDate(v) { if (!v) return ''; const d = new Date(v); return isNaN(d) ? '' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
function relTime(v) { if (!v) return ''; const d = new Date(v); if (isNaN(d)) return ''; const diff = Date.now() - d.getTime(); const day = 86400000; if (diff < day) return 'Today'; if (diff < 2 * day) return 'Yesterday'; if (diff < 30 * day) return Math.floor(diff / day) + ' days ago'; return ukDate(v); }
function parseSteps(v) { if (!v) return []; try { return (typeof v === 'string' ? JSON.parse(v) : v) || []; } catch { return []; } }

function RecordFavicon({ name, website }) {
  const [failed, setFailed] = useState(false);
  const dom = domainOf(website);
  if (!dom || failed) return <span className="crmr-cofav" style={{ background: 'linear-gradient(135deg,#FF7A59,#EC4899)' }}>{initials(name)}</span>;
  return <span className="crmr-cofav"><img src={`https://www.google.com/s2/favicons?domain=${dom}&sz=64`} alt="" onError={() => setFailed(true)} /></span>;
}

const TABS = ['Activity', 'Intelligence', 'Signals', 'Sequences', 'Notes', 'Emails'];

export default function CrmContactRecord() {
  const params = useParams();
  const clientId = params.id;
  const contactId = params.contactId;
  const location = useLocation();
  const fromPath = location.state?.from || null;
  const navigate = useNavigate();
  const ctx = useOutletContext() || {};
  const baseClientId = ctx.clientId || clientId;
  const { getToken } = useAuth();
  const { user } = useUser();
  const userEmail = user?.primaryEmailAddress?.emailAddress || '';

  const [detail, setDetail] = useState(null);
  const [channels, setChannels] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('Activity');
  const [expandedSeqId, setExpandedSeqId] = useState(null);
  const [status, setStatus] = useState('new');
  const [enriching, setEnriching] = useState(false);
  const [deepEnriching, setDeepEnriching] = useState(false);
  const [showDeepConfirm, setShowDeepConfirm] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [addForm, setAddForm] = useState({ linkedin_url: '', full_name: '', job_title: '', email: '', photo_url: '' });
  const [fetchingLi, setFetchingLi] = useState(false);
  const [creatingContact, setCreatingContact] = useState(false);
  const [deals, setDeals] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [dealDrawer, setDealDrawer] = useState(null);
  const [compose, setCompose] = useState(null); // null | { replyTo }
  const [emailRefresh, setEmailRefresh] = useState(0);
  const [emailActivity, setEmailActivity] = useState([]);
  const [contactTz, setContactTz] = useState(null);

  const refetchDetail = useCallback(async () => { const r = await fetch(`${API_BASE}/contacts/${contactId}/detail`); if (r.ok) setDetail(await r.json()); }, [contactId]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API_BASE}/contacts/${contactId}/detail`);
      if (!res.ok) throw new Error('Failed to load contact (' + res.status + ')');
      const data = await res.json();
      setDetail(data);
      setStatus(data.contact?.status || 'new');
      if (data.sequences?.length) { const newest = [...data.sequences].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]; setExpandedSeqId(newest.id); }
      try { const chR = await fetch(`${API_BASE}/contacts/${contactId}/sequence-channels`); if (chR.ok) setChannels(await chR.json()); } catch (e) {}
      try { const nR = await fetch(`${API_BASE}/contacts/${contactId}/notes`); const nd = await nR.json(); setNotes(Array.isArray(nd) ? nd : []); } catch (e) {}
      try { const dR = await fetch(`${API_BASE}/clients/${baseClientId}/opportunities?contact_id=${contactId}`); if (dR.ok) setDeals(await dR.json()); } catch (e) {}
      try { const pR = await fetch(`${API_BASE}/clients/${baseClientId}/pipelines`); if (pR.ok) setPipelines(await pR.json()); } catch (e) {}
      try { const eR = await fetch(`${API_BASE}/contacts/${contactId}/email-activity`); if (eR.ok) { const ed = await eR.json(); setEmailActivity(Array.isArray(ed.items) ? ed.items : []); setContactTz(ed.timezone || null); } } catch (e) {}
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [contactId]);
  useEffect(() => { load(); }, [load]);

  const backToList = () => navigate(fromPath || '/clients/' + baseClientId + '/crm/contacts');
  const getChannel = (ch) => channels.find(x => x.channel === ch) || null;

  async function changeStatus(s) { setStatus(s); await fetch(`${API_BASE}/contacts/${contactId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: s }) }).catch(() => {}); }

  async function enrich(deep) {
    if (deep) setDeepEnriching(true); else setEnriching(true);
    const tId = toast.loading(deep ? 'Deep enriching…' : 'Enriching…');
    try {
      const res = await creditAwareFetch(`${API_BASE}/contacts/${contactId}/enrich`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ deep }) });
      if (!res) { toast.dismiss(tId); } else { const data = await res.json(); if (data.success) { await refetchDetail(); toast.success(deep ? 'Deep enriched' : 'Enriched', { id: tId }); } else toast.error('Could not enrich', { id: tId }); }
    } catch (e) { toast.error('Enrichment failed', { id: tId }); }
    if (deep) setDeepEnriching(false); else setEnriching(false);
  }

  async function score() {
    setScoring(true); const tId = toast.loading('Scoring…');
    try { await fetch(`${API_BASE}/contacts/${contactId}/score`, { method: 'POST', headers: { 'Content-Type': 'application/json' } }); await refetchDetail(); toast.success('Scored', { id: tId }); }
    catch (e) { toast.error('Scoring failed', { id: tId }); }
    setScoring(false);
  }

  async function generateSequence() {
    setGenerating(true); const tId = toast.loading('Generating sequence — up to 60 seconds…', { duration: 90000 });
    try {
      const r = await creditAwareFetch(`${API_BASE}/contacts/${contactId}/generate-sequence`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ contact_id: contactId, client_id: baseClientId }) });
      if (!r) { toast.dismiss(tId); setGenerating(false); return; }
      if (!r.ok) { toast.error('Generation failed', { id: tId }); setGenerating(false); return; }
      const seq = await r.json();
      await fetch(`${API_BASE}/contacts/${contactId}/sequences`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ client_id: baseClientId, email_sequence: seq.email_sequence, linkedin_sequence: seq.linkedin_sequence, call_sequence: seq.call_sequence }) });
      await refetchDetail();
      try { const chR = await fetch(`${API_BASE}/contacts/${contactId}/sequence-channels`); if (chR.ok) setChannels(await chR.json()); } catch (e) {}
      toast.success('Sequence generated', { id: tId }); setTab('Sequences');
    } catch (e) { toast.error('Generation failed', { id: tId }); }
    setGenerating(false);
  }

  async function approveChannel(channelId) {
    try { const r = await fetch(`${API_BASE}/sequence-channels/${channelId}/approve`, { method: 'PUT' }); if (!r.ok) throw new Error('Approve failed'); const chR = await fetch(`${API_BASE}/contacts/${contactId}/sequence-channels`); if (chR.ok) setChannels(await chR.json()); await refetchDetail(); toast.success('Channel approved'); }
    catch (e) { toast.error(e.message); }
  }

  function startEdit(c) { setEditForm({ full_name: c.full_name || '', job_title: c.job_title || '', email: c.email || '', phone: c.phone || '', linkedin_url: c.linkedin_url || '', location: c.location || '' }); setEditing(true); }

  function openAddContact() { setAddForm({ linkedin_url: '', full_name: '', job_title: '', email: '', photo_url: '' }); setShowAddContact(true); }
  async function fetchFromLinkedin() {
    const url = addForm.linkedin_url.trim();
    if (!url.toLowerCase().includes('linkedin.com/in/')) { toast.error('Enter a valid LinkedIn profile URL'); return; }
    setFetchingLi(true);
    const tId = toast.loading('Reading LinkedIn profile…');
    try {
      const r = await fetch(`${API_BASE}/parse-linkedin`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ linkedin_url: url }) });
      const d = await r.json();
      if (!r.ok) { toast.error(d.error || 'Could not read that profile', { id: tId }); }
      else if (d.found) { setAddForm(p => ({ ...p, full_name: d.full_name || p.full_name, job_title: d.job_title || p.job_title, photo_url: d.photo_url || p.photo_url })); toast.success('Prefilled from LinkedIn', { id: tId }); }
      else { toast('No details found — fill the form manually', { id: tId }); }
    } catch (e) { toast.error('Lookup failed', { id: tId }); }
    setFetchingLi(false);
  }
  async function createContact() {
    if (!addForm.full_name.trim()) { toast.error('Name is required'); return; }
    if (!c.company_id) { toast.error('No company to attach to'); return; }
    setCreatingContact(true);
    try {
      const r = await fetch(`${API_BASE}/companies/${c.company_id}/contacts`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ client_id: baseClientId, full_name: addForm.full_name.trim(), job_title: addForm.job_title.trim(), email: addForm.email.trim(), linkedin_url: addForm.linkedin_url.trim(), photo_url: addForm.photo_url || '' }) });
      const d = await r.json();
      if (r.status === 409) { toast.error(d.message || 'That contact already exists'); setCreatingContact(false); if (d.existing_id) { setShowAddContact(false); navigate('/clients/' + baseClientId + '/crm/contacts/' + d.existing_id); } return; }
      if (!r.ok) { toast.error(d.error || 'Could not create contact'); setCreatingContact(false); return; }
      toast.success('Contact added');
      setShowAddContact(false);
      navigate('/clients/' + baseClientId + '/crm/contacts/' + d.id);
    } catch (e) { toast.error('Could not create contact'); }
    setCreatingContact(false);
  }
  async function saveEdit() {
    setSavingEdit(true);
    try { const r = await fetch(`${API_BASE}/contacts/${contactId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(editForm) }); if (!r.ok) throw new Error('save failed'); await refetchDetail(); setEditing(false); toast.success('Contact updated'); }
    catch (e) { toast.error('Could not save'); }
    setSavingEdit(false);
  }

  async function postNote() {
    if (!newNote.trim()) return; setSavingNote(true);
    try { const res = await fetch(`${API_BASE}/contacts/${contactId}/notes`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ client_id: baseClientId, author_email: userEmail, author_name: userEmail?.split('@')[0] || 'You', content: newNote.trim() }) }); const note = await res.json(); setNotes(prev => [...prev, note]); setNewNote(''); }
    catch (e) {}
    setSavingNote(false);
  }
  async function deleteNote(noteId) { try { await fetch(`${API_BASE}/contacts/${contactId}/notes/${noteId}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ user_email: userEmail }) }); setNotes(prev => prev.filter(n => n.id !== noteId)); } catch (e) {} }

  async function del() {
    if (!window.confirm('Delete this contact? This cannot be undone.')) return;
    try { const token = await getToken(); const r = await fetch(`${API_BASE}/contacts/${contactId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }); if (!r.ok) throw new Error('delete failed'); toast.success('Contact deleted'); backToList(); }
    catch (e) { toast.error('Delete failed'); }
  }

  if (loading) return <div className="crmr-wrap"><div className="crmr-empty">Loading contact…</div></div>;
  if (error || !detail || !detail.contact) return <div className="crmr-wrap"><button className="crmr-back" onClick={backToList}>{fromPath ? '← Back' : '← Contacts'}</button><div className="crmr-empty">{error || 'Contact not found.'}</div></div>;

  const c = detail.contact;
  const signals = detail.signals || [];
  const sequences = detail.sequences || [];
  const sc = detail.score || null;
  const totalScore = sc ? sc.total_score : null;
  const band = scoreBand(totalScore);
  const enrich_ = c.enrichment_data || {};
  const avColour = avatarColour(c.full_name);
  const liUrl = c.linkedin_url ? (c.linkedin_url.startsWith('http') ? c.linkedin_url : 'https://' + c.linkedin_url) : null;

  const timeline = [];
  for (const s of signals.slice(0, 4)) { const m = SIGNAL[s.signal_type] || SIGNAL.news; timeline.push({ when: s.detected_at, colour: m.c, bg: m.bg, icon: m.icon, title: m.l + ' signal detected', text: (s.signal_data && s.signal_data.summary) || '' }); }
  for (const sq of sequences.slice(0, 3)) timeline.push({ when: sq.created_at, colour: '#BE2A6E', bg: '#FBE3F0', icon: '↻', title: 'Sequence generated · v' + (sq.version || 1), text: sq.approval_status ? 'Status: ' + sq.approval_status : '' });
  if (c.enriched_at) timeline.push({ when: c.enriched_at, colour: '#B26B00', bg: '#FBEACF', icon: '◷', title: 'Enriched', text: 'Profile enriched from research.' });
  if (c.created_at) timeline.push({ when: c.created_at, colour: '#3F4956', bg: '#E3E7ED', icon: '＋', title: 'Contact created', text: '' });
  for (const it of emailActivity) {
    const where = [it.campaign_name ? it.campaign_name : (it.manual ? '1-1 email' : null), it.step != null && it.campaign_name ? 'step ' + it.step : null].filter(Boolean).join(' · ');
    const at = fmtWhen(it.at);
    if (it.type === 'sent') timeline.push({ when: it.at, colour: '#0B5BB5', bg: '#D9E9FB', icon: '✉', title: 'Email sent' + (where ? ' · ' + where : ''), text: [it.subject, at].filter(Boolean).join(' · ') });
    else if (it.type === 'opened') timeline.push({ when: it.last_at || it.at, colour: '#056F4E', bg: '#D1F0E4', icon: '◉', title: 'Opened' + (it.count > 1 ? ' ' + it.count + '×' : '') + (where ? ' · ' + where : ''), text: (it.count > 1 ? 'First ' + at + ', last ' + fmtWhen(it.last_at) : at) + (it.clients && it.clients.length ? ' · ' + it.clients.join(', ') : '') });
    else if (it.type === 'clicked') timeline.push({ when: it.at, colour: '#056F4E', bg: '#D1F0E4', icon: '↗', title: 'Clicked a link' + (where ? ' · ' + where : ''), text: [(it.url || '').replace(/^https?:\/\//, '').slice(0, 60), at].filter(Boolean).join(' · ') });
    else if (it.type === 'replied') timeline.push({ when: it.at, colour: '#5B3FD6', bg: '#E7E1FD', icon: '↩', title: 'Replied' + (where ? ' · ' + where : ''), text: [it.subject, at].filter(Boolean).join(' · ') });
  }
  timeline.sort((a, b) => new Date(b.when || 0) - new Date(a.when || 0));

  const busy = enriching || deepEnriching;

  return (
    <div className="crmr-wrap">
      <div className="crmr-topbar"><button className="crmr-back" onClick={backToList}>{fromPath ? '← Back' : '← Contacts'}</button><span className="crmr-crumb">{c.full_name}</span></div>

      <div className="crmr-actionbar">
        <button className="crmr-act crmr-act-primary" onClick={generateSequence} disabled={generating}>{generating ? 'Generating…' : sequences.length ? 'Regenerate sequence' : 'Generate sequence'}</button>
        <button className="crmr-act crmr-act-blue" onClick={() => setCompose({ replyTo: null })} disabled={!c.email} title={c.email ? 'Send a 1-1 email from your inbox' : 'Add an email address first'}>Email</button>
        <button className="crmr-act crmr-act-violet" onClick={() => enrich(false)} disabled={busy}>{enriching ? 'Enriching…' : enrich_.insight ? 'Re-enrich' : 'Enrich'}</button>
        <button className="crmr-act crmr-act-rose" onClick={() => !busy && setShowDeepConfirm(true)} disabled={busy} title="Extended web research — 6 credits">{deepEnriching ? 'Deep enriching…' : 'Deep enrich'}</button>
        <button className="crmr-act crmr-act-blue" onClick={score} disabled={scoring}>{scoring ? 'Scoring…' : sc ? 'Re-score' : 'Score'}</button>
        <button className="crmr-act crmr-act-ghost" onClick={() => navigate('/clients/' + baseClientId + '/crm/find-similar/contacts/' + c.id, { state: { seedLabel: c.full_name } })}>Find similar →</button>
        {liUrl && <a className="crmr-act crmr-act-blue" href={liUrl} target="_blank" rel="noreferrer">LinkedIn →</a>}
        <button className="crmr-act crmr-act-danger" onClick={del}>Delete</button>
      </div>

      <div className="crmr-grid">
        <div className="crmr-col crmr-col-left">
          <div className="crmr-idcard">
            <div className="crmr-idtop">
              <span className="crmr-avatar" style={{ background: avColour }}>{c.photo_url ? <img src={c.photo_url} alt="" className="crmr-avatar-img" onError={(e) => { e.target.style.display = 'none'; }} /> : initials(c.full_name)}</span>
              {totalScore != null && <span className={'crmr-scorebadge ' + band.cls}><span className="crmr-scoren">{totalScore}</span><span className="crmr-scorel">{band.label}</span></span>}
            </div>
            <h1 className="crmr-name">{c.full_name}</h1>
            <p className="crmr-role">{c.job_title || 'Unknown role'}{c.company_name ? <> · <b>{c.company_name}</b></> : null}</p>
            <div className="crmr-statusrow"><select className="crmr-statussel" value={status} onChange={e => changeStatus(e.target.value)}>{STATUSES.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
          </div>

          <div className="crmr-card">
            <div className="crmr-cardh">Key information {!editing && <button className="crmr-cardh-edit" onClick={() => startEdit(c)}>Edit</button>}</div>
            {editing ? (
              <div className="crmr-editform">
                {[['Full name', 'full_name'], ['Job title', 'job_title'], ['Email', 'email'], ['Phone', 'phone'], ['LinkedIn URL', 'linkedin_url'], ['Location', 'location']].map(([label, key]) => (
                  <div key={key} className="crmr-editfield"><label>{label}</label><input value={editForm[key] || ''} onChange={e => setEditForm(p => ({ ...p, [key]: e.target.value }))} /></div>
                ))}
                <div className="crmr-editactions"><button className="crmr-act crmr-act-primary" onClick={saveEdit} disabled={savingEdit}>{savingEdit ? 'Saving…' : 'Save changes'}</button><button className="crmr-act crmr-act-ghost" onClick={() => setEditing(false)}>Cancel</button></div>
              </div>
            ) : (
              <div className="crmr-props">
                <Prop k="Email" v={c.email ? <button type="button" className="crmr-link-teal crmr-linkbtn" onClick={() => setCompose({ replyTo: null })} title="Write an email">{c.email}</button> : '—'} />
                <Prop k="Phone" v={c.phone || '—'} />
                <Prop k="LinkedIn" v={liUrl ? <a href={liUrl} target="_blank" rel="noreferrer" className="crmr-link">{(c.linkedin_url || '').replace(/^https?:\/\/(www\.)?linkedin\.com\//, '')}</a> : '—'} />
                <Prop k="Location" v={c.location || '—'} />
                <Prop k="Timezone" v={contactTz && contactTz.tz ? `${contactTz.tz.replace(/_/g, ' ')} · ${new Date().toLocaleTimeString('en-GB', { timeZone: contactTz.tz, hour: '2-digit', minute: '2-digit' })} now${contactTz.source === 'company_location' ? ' (from company)' : ''}` : 'Unknown, campaign timezone used'} />
                <Prop k="Status" v={<span className={'crmr-pill ' + (STATUS_PILL[status] || 'crmr-p-blue')}>{status}</span>} />
                <Prop k="Email engagement" v={engagementSummary(emailActivity)} />
                <Prop k="Source" v={c.source || '—'} />
                <Prop k="Created" v={ukDate(c.created_at) || '—'} />
              </div>
            )}
          </div>

          {sc && (
            <div className="crmr-card">
              <div className="crmr-cardh">Score breakdown</div>
              <div className="crmr-scorebreak">
                <div className="crmr-scorecells">
                  {[['ICP', sc.icp_score, '40%'], ['Signals', sc.signal_score, '35%'], ['Enrichment', sc.enrichment_score, '25%']].map(([l, v, w]) => (
                    <div key={l} className="crmr-scorecell"><div className="crmr-scorecell-n">{v ?? '—'}</div><div className="crmr-scorecell-l">{l}</div><div className="crmr-scorecell-w">{w}</div></div>
                  ))}
                </div>
                <div className="crmr-scoretotal"><span>Total score</span><b>{sc.total_score}/10</b></div>
              </div>
            </div>
          )}
        </div>

        <div className="crmr-col crmr-col-mid">
          <div className="crmr-card crmr-activity">
            <div className="crmr-tabs">
              {TABS.map(t => (<button key={t} className={'crmr-tab' + (tab === t ? ' crmr-tab--on' : '')} onClick={() => setTab(t)}>{t}{t === 'Signals' && signals.length ? ` (${signals.length})` : ''}{t === 'Sequences' && sequences.length ? ` (${sequences.length})` : ''}</button>))}
            </div>

            {tab === 'Activity' && (
              <div className="crmr-timeline">
                {timeline.length === 0 && <div className="crmr-empty-sm">No activity yet.</div>}
                {timeline.map((it, i) => (<div className="crmr-tlitem" key={i}><span className="crmr-tldot" style={{ background: it.bg, color: it.colour }}>{it.icon}</span><div className="crmr-tlbody"><div className="crmr-tltop"><span className="crmr-tlwho">{it.title}</span><span className="crmr-tlwhen">{relTime(it.when)}</span></div>{it.text && <div className="crmr-tltext">{it.text}</div>}</div></div>))}
              </div>
            )}

            {tab === 'Intelligence' && (
              <div className="crmr-intel">
                {!enrich_.insight && <div className="crmr-empty-sm">No intelligence yet. Use Enrich to generate insight for this contact.</div>}
                {enrich_.insight && <div className="crmr-intelblock crmr-intelblock-violet"><div className="crmr-intelh">Insight</div><p>{enrich_.insight}</p></div>}
                {enrich_.buying_signals && <div className="crmr-intelblock crmr-intelblock-amber"><div className="crmr-intelh">Buying signals</div><p>{enrich_.buying_signals}</p></div>}
                {enrich_.recent_activity && <div className="crmr-intelblock crmr-intelblock-blue"><div className="crmr-intelh">Recent activity</div><p>{enrich_.recent_activity}</p></div>}
                {Array.isArray(enrich_.recent_posts) && enrich_.recent_posts.length > 0 && (
                  <div className="crmr-intelblock crmr-intelblock-grey">
                    <div className="crmr-intelh">Recent posts</div>
                    {enrich_.recent_posts.slice(0, 3).map((p, i) => (<div className="crmr-post" key={i}><div className="crmr-postmeta">{p.date && <span>{ukDate(p.date)}</span>}{p.source && <span className="crmr-chip crmr-chip-slate">{p.source}</span>}{p.url && <a href={p.url} target="_blank" rel="noreferrer" className="crmr-link">view ↗</a>}</div><p>{(p.text || '').substring(0, 220)}{(p.text || '').length > 220 ? '…' : ''}</p></div>))}
                  </div>
                )}
              </div>
            )}

            {tab === 'Signals' && (
              <div className="crmr-siglist">
                {signals.length === 0 && <div className="crmr-empty-sm">No signals detected for {c.company_name} in the last 90 days.</div>}
                {signals.map(s => { const m = SIGNAL[s.signal_type] || SIGNAL.news; return (
                  <div className="crmr-sigcard" key={s.id} style={{ background: m.bg, borderLeft: `3px solid ${m.c}` }}>
                    <div className="crmr-sigcard-top"><span className="crmr-sigcard-l" style={{ color: m.c }}>{m.l}</span>{s.relevance_score != null && <span className="crmr-sigcard-s" style={{ color: m.c }}>Score {s.relevance_score}/10</span>}</div>
                    <p className="crmr-sigcard-sum">{(s.signal_data && s.signal_data.summary) || ''}</p>
                    <p className="crmr-sigcard-src">{s.source_url ? <a href={s.source_url} target="_blank" rel="noreferrer" className="crmr-link">{s.source}</a> : s.source}{s.detected_at ? ' · ' + ukDate(s.detected_at) : ''}</p>
                  </div>
                ); })}
              </div>
            )}

            {tab === 'Sequences' && (
              <div className="crmr-seqwrap">
                <CrmSequenceSettings contactId={contactId} />
                <button className="crmr-act crmr-act-primary crmr-seqgen" onClick={generateSequence} disabled={generating}>{generating ? 'Generating…' : sequences.length ? 'Regenerate sequence' : 'Generate sequence'}</button>
                {sequences.length === 0 && <div className="crmr-empty-sm">No sequences generated yet.</div>}
                {sequences.map(seq => {
                  const emailSteps = parseSteps(seq.email_sequence); const linkedinSteps = parseSteps(seq.linkedin_sequence);
                  const open = expandedSeqId === seq.id; const emCh = getChannel('email'); const liCh = getChannel('linkedin');
                  return (
                    <div className="crmr-seqcard" key={seq.id}>
                      <div className="crmr-seqhead" onClick={() => setExpandedSeqId(open ? null : seq.id)}>
                        <div><div className="crmr-seqver">Version {seq.version || 1}</div><div className="crmr-seqcreated">Created {ukDate(seq.created_at)}</div></div>
                        <div className="crmr-seqpills">
                          {emCh && <span className={'crmr-pill ' + (emCh.approval_status === 'approved' ? 'crmr-p-green' : 'crmr-p-amber')}>Em · {emCh.approval_status}</span>}
                          {emCh && emCh.approval_status !== 'approved' && <button className="crmr-approvebtn crmr-approve-green" onClick={(e) => { e.stopPropagation(); approveChannel(emCh.id); }}>Approve email</button>}
                          {liCh && <span className={'crmr-pill ' + (liCh.approval_status === 'approved' ? 'crmr-p-green' : 'crmr-p-blue')}>Li · {liCh.approval_status}</span>}
                          {liCh && liCh.approval_status !== 'approved' && <button className="crmr-approvebtn crmr-approve-blue" onClick={(e) => { e.stopPropagation(); approveChannel(liCh.id); }}>Approve LinkedIn</button>}
                          {seq.pushed_at && <span className="crmr-pushed">Pushed</span>}
                        </div>
                      </div>
                      {open && emailSteps.length > 0 && (<div className="crmr-seqsteps"><div className="crmr-seqsteph">Email sequence</div>{emailSteps.map((step, i) => (<div className="crmr-step" key={i}><div className="crmr-step-n">Step {step.step}</div>{step.subject && <div className="crmr-step-subj">{step.subject}</div>}<div className="crmr-step-body">{step.body}</div></div>))}</div>)}
                      {open && linkedinSteps.length > 0 && (<div className="crmr-seqsteps"><div className="crmr-seqsteph">LinkedIn sequence</div>{linkedinSteps.map((step, i) => (<div className="crmr-step" key={i}><div className="crmr-step-n">Step {step.step}{step.type ? ' — ' + step.type.replace(/_/g, ' ') : ''}</div><div className="crmr-step-body">{step.message}</div></div>))}</div>)}
                    </div>
                  );
                })}
              </div>
            )}

            {tab === 'Notes' && (
              <div className="crmr-notes">
                <div className="crmr-notelist">
                  {notes.length === 0 && <div className="crmr-empty-sm">No notes yet. Add the first one below.</div>}
                  {notes.map(note => { const isMe = note.author_email === userEmail; return (
                    <div className={'crmr-notebubble-row' + (isMe ? ' crmr-note-me' : '')} key={note.id}>
                      <span className="crmr-noteav" style={{ background: isMe ? '#FF7A59' : avatarColour(note.author_name || note.author_email) }}>{initials(note.author_name || note.author_email)}</span>
                      <div className="crmr-notebody">
                        <div className="crmr-notemeta"><span>{note.author_name || note.author_email}</span><span className="crmr-notetime">{note.created_at ? new Date(note.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span></div>
                        <div className={'crmr-notebubble' + (isMe ? ' crmr-notebubble-me' : '')} style={isMe ? undefined : (() => { const ac = avatarColour(note.author_name || note.author_email); return { background: ac + '22', color: '#16181D', borderLeft: `3px solid ${ac}` }; })()}>{note.content}{isMe && <button className="crmr-notedel" onClick={() => deleteNote(note.id)}>×</button>}</div>
                      </div>
                    </div>
                  ); })}
                </div>
                <div className="crmr-notecompose"><textarea value={newNote} onChange={e => setNewNote(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); postNote(); } }} placeholder="Add a note… (Enter to send, Shift+Enter for new line)" rows={2} /><button className="crmr-act crmr-act-primary" onClick={postNote} disabled={savingNote || !newNote.trim()}>{savingNote ? '…' : 'Send'}</button></div>
              </div>
            )}

            {tab === 'Emails' && (
              <div className="crmr-emails">
                <div className="crmr-emails-head">
                  <span>{c.email ? c.email : 'No email address on this contact'}</span>
                  <button className="crmr-act crmr-act-primary" onClick={() => setCompose({ replyTo: null })} disabled={!c.email}>New email</button>
                </div>
                <CrmEmailTimeline
                  url={`${API_BASE}/conversations/by-contact/${contactId}`}
                  refreshKey={emailRefresh}
                  onReply={(m) => setCompose({ replyTo: { type: m.direction === 'inbound' ? 'inbound' : 'outbound', id: m.id, subject: m.subject } })}
                  emptyLabel="No emails yet. Emails you send from here, campaign emails and replies all appear on this record."
                />
              </div>
            )}
          </div>
        </div>

        <div className="crmr-col crmr-col-right">
          <div className="crmr-card">
            <div className="crmr-cardh">Company{c.company_id && <button className="crmr-cardh-add" onClick={openAddContact} title="Add another contact at this company">+ Add contact</button>}</div>
            {c.company_name ? (
              <div className="crmr-company"><RecordFavicon name={c.company_name} website={c.website} /><div className="crmr-cobody"><div className="crmr-coname">{c.company_name}</div>{c.sector && <div className="crmr-cometa">{c.sector}</div>}{c.website && <a className="crmr-link" href={c.website.startsWith('http') ? c.website : 'https://' + c.website} target="_blank" rel="noreferrer">{domainOf(c.website)}</a>}<div className="crmr-cotags">{c.size && <span className="crmr-chip crmr-chip-slate">{c.size}</span>}{c.location && <span className="crmr-chip crmr-chip-slate">{c.location}</span>}</div></div></div>
            ) : <div className="crmr-empty-sm">No company linked.</div>}
          </div>

          <div className="crmr-card">
            <div className="crmr-cardh">Buying signals {totalScore != null && <span className="crmr-cardh-x">signal score {sc.signal_score != null ? sc.signal_score : '—'}/10</span>}</div>
            <div className="crmr-signals">
              {signals.length === 0 && <div className="crmr-empty-sm">No signals in the last 90 days.</div>}
              {signals.slice(0, 5).map(s => { const m = SIGNAL[s.signal_type] || SIGNAL.news; return (
                <div className="crmr-sigrow" key={s.id}><span className="crmr-sigico" style={{ background: m.bg, color: m.c }}>{m.icon}</span><div className="crmr-sigbody"><span className="crmr-sigchip" style={{ background: m.bg, color: m.c }}>{m.l}</span><div className="crmr-sigtext">{(s.signal_data && s.signal_data.summary) || m.l}</div>{s.detected_at && <div className="crmr-sigdate">{relTime(s.detected_at)}</div>}</div></div>
              ); })}
            </div>
          </div>

          <div className="crmr-card">
            <div className="crmr-cardh">Deals <span className="crmr-cardh-x">{deals.length}</span><button className="crmr-cardh-add" onClick={() => setDealDrawer({})} title="Add a deal">+ Add</button></div>
            {deals.length === 0 ? (
              <div className="crmr-dealempty"><div className="crmr-dealempty-ico">◇</div><p>No deals yet. Track revenue opportunities for this contact here.</p></div>
            ) : (
              <div className="crmr-deallist">
                {deals.map(d => {
                  const sym = { GBP: '\u00A3', USD: '$', EUR: '\u20AC', NGN: '\u20A6', CAD: 'CA$', AUD: 'A$' }[d.currency] || '\u00A3';
                  return (
                    <button key={d.id} className="crmr-dealrow" onClick={() => navigate(`/clients/${baseClientId}/crm/opportunities/${d.id}`)}>
                      <span className="crmr-dealdot" style={{ background: d.stage_colour || '#64748B' }} />
                      <span className="crmr-dealnm">{d.name}</span>
                      <span className="crmr-dealstage">{d.stage_name}</span>
                      <span className="crmr-dealval">{sym}{Number(d.value || 0).toLocaleString()}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="crmr-card">
            <div className="crmr-cardh">Attachments<button className="crmr-cardh-add" onClick={() => toast('Attachments are coming soon')} title="Add an attachment">+ Add</button></div>
            <div className="crmr-dealempty"><div className="crmr-dealempty-ico">▢</div><p>No attachments yet. Files linked to this contact will appear here.</p></div>
          </div>

          <div className="crmr-card">
            <div className="crmr-cardh">Tickets <span className="crmr-cardh-x">0</span></div>
            <div className="crmr-dealempty"><div className="crmr-dealempty-ico">◷</div><p>Ticketing is coming soon. Customer requests for this contact will appear here.</p></div>
          </div>
        </div>
      </div>

      {showDeepConfirm && (
        <div className="crmr-modal-overlay" onClick={() => !deepEnriching && setShowDeepConfirm(false)}>
          <div className="crmr-modal" onClick={e => e.stopPropagation()}>
            <h2>Deep enrich this contact?</h2>
            <p>Deep enrich uses extended web research to surface signals standard enrichment may miss. Worth it for high-value contacts.</p>
            <div className="crmr-modal-cost"><span>Credits</span><b>6 credits</b><div className="crmr-modal-note">Standard enrich is 3 credits and works well for most contacts.</div></div>
            <div className="crmr-modal-actions"><button className="crmr-act crmr-act-ghost" onClick={() => setShowDeepConfirm(false)} disabled={deepEnriching}>Cancel</button><button className="crmr-act crmr-act-primary" onClick={() => { setShowDeepConfirm(false); enrich(true); }} disabled={deepEnriching}>{deepEnriching ? 'Starting…' : 'Deep enrich'}</button></div>
          </div>
        </div>
      )}

      {showAddContact && (
        <div className="crmr-modal-overlay" onClick={() => !creatingContact && !fetchingLi && setShowAddContact(false)}>
          <div className="crmr-modal crmr-modal-wide" onClick={e => e.stopPropagation()}>
            <h2>Add contact to {c.company_name}</h2>
            <p>Paste a LinkedIn profile to prefill, or enter the details by hand. The new contact is attached to this company automatically.</p>
            <div className="crmr-addfield">
              <label>LinkedIn profile URL</label>
              <div className="crmr-addli">
                <input value={addForm.linkedin_url} onChange={e => setAddForm(p => ({ ...p, linkedin_url: e.target.value }))} placeholder="https://www.linkedin.com/in/…" />
                <button className="crmr-act crmr-act-violet" onClick={fetchFromLinkedin} disabled={fetchingLi}>{fetchingLi ? 'Reading…' : 'Fetch'}</button>
              </div>
            </div>
            <div className="crmr-addfield"><label>Full name *</label><input value={addForm.full_name} onChange={e => setAddForm(p => ({ ...p, full_name: e.target.value }))} placeholder="Jane Doe" /></div>
            <div className="crmr-addfield"><label>Job title</label><input value={addForm.job_title} onChange={e => setAddForm(p => ({ ...p, job_title: e.target.value }))} placeholder="Head of Revenue" /></div>
            <div className="crmr-addfield"><label>Email</label><input value={addForm.email} onChange={e => setAddForm(p => ({ ...p, email: e.target.value }))} placeholder="jane@company.com" /></div>
            <div className="crmr-modal-actions">
              <button className="crmr-act crmr-act-ghost" onClick={() => setShowAddContact(false)} disabled={creatingContact}>Cancel</button>
              <button className="crmr-act crmr-act-primary" onClick={createContact} disabled={creatingContact}>{creatingContact ? 'Adding…' : 'Add contact'}</button>
            </div>
          </div>
        </div>
      )}

      {compose && (
        <CrmEmailComposer
          clientId={baseClientId}
          contact={{ id: c.id, full_name: c.full_name, email: c.email }}
          replyTo={compose.replyTo}
          onClose={() => setCompose(null)}
          onSent={() => { setCompose(null); setTab('Emails'); setEmailRefresh(k => k + 1); if (status === 'new') setStatus('contacted'); }}
        />
      )}

      {dealDrawer && (
        <DealDrawer
          opp={dealDrawer.opp}
          pipelines={pipelines}
          companies={c.company_id ? [{ id: c.company_id, name: c.company_name, sector: c.sector }] : []}
          contacts={[{ id: c.id, full_name: c.full_name, job_title: c.job_title, company_id: c.company_id, photo_url: c.photo_url }]}
          prefillCompanyId={c.company_id || null}
          prefillContactId={c.id}
          lockCompany
          lockContact
          clientId={baseClientId}
          onClose={() => setDealDrawer(null)}
          onSaved={() => { setDealDrawer(null); load(); }}
        />
      )}
    </div>
  );
}

function fmtWhen(v) {
  if (!v) return '';
  const d = new Date(v);
  return isNaN(d) ? '' : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
// "Opened 4×, last 31 Oct 09:40 · clicked 1× · replied" across every email to this contact.
function engagementSummary(items) {
  const sent = items.filter(i => i.type === 'sent').length;
  if (!sent) return '—';
  const opens = items.filter(i => i.type === 'opened');
  const openN = opens.reduce((n, i) => n + (i.count || 1), 0);
  const lastOpen = opens.reduce((m, i) => (!m || new Date(i.last_at || i.at) > new Date(m) ? (i.last_at || i.at) : m), null);
  const clickN = items.filter(i => i.type === 'clicked').length;
  const replied = items.some(i => i.type === 'replied');
  const parts = [`${sent} sent`];
  parts.push(openN ? `opened ${openN}×, last ${fmtWhen(lastOpen)}` : 'not opened');
  if (clickN) parts.push(`clicked ${clickN}×`);
  if (replied) parts.push('replied');
  return parts.join(' · ');
}

function Prop({ k, v }) { return <div className="crmr-prop"><span className="crmr-prop-k">{k}</span><span className="crmr-prop-v">{v}</span></div>; }
